/* eslint-disable max-lines */

import SelectRedesign from 'components/Select/SelectRedesign';
import useClickOutside from 'components/utils/useClickOutside';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { DateRange, DayPicker, getDefaultClassNames, TZDate, useDayPicker } from 'react-day-picker';
import 'react-day-picker/src/style.css';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { getSyncDates } from 'selectors/auth';
import { useSelector } from 'store';

type ComponentProps = {
  containerRef: React.RefObject<HTMLElement>;
  defaultRange: DateRange;
  setShowCalendar: (value: boolean) => void;
  setDateRange: (range: DateRange) => void;
  dateOption: string;
  timeZone?: string;
  disableFutureDates?: boolean;
};

const months = [
  'Jan',
  'Feb',
  'March',
  'April',
  'May',
  'June',
  'July',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

const CustomNextMonthButton: React.FC<{ single?: boolean; disableNextMonth?: boolean }> = (
  props
) => {
  const { single = false, disableNextMonth = true } = props;
  const dayPickerContext = useDayPicker();
  const nextMonth = moment(dayPickerContext.nextMonth).endOf('month');
  const current = moment();

  let disabled = single
    ? nextMonth.subtract(1, 'month').isAfter(current)
    : nextMonth.isAfter(current);

  if (!disableNextMonth) disabled = false;

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <button {...props} type="button" disabled={disabled}>
      <svg className="rdp-chevron" width="24" height="24" viewBox="0 0 24 24">
        <polygon points="8 18.612 14.1888889 12.5 8 6.37733333 9.91111111 4.5 18 12.5 9.91111111 20.5" />
      </svg>
    </button>
  );
};

const DateRangePicker: React.FC<ComponentProps> = (props) => {
  const {
    containerRef,
    defaultRange,
    setShowCalendar,
    setDateRange,
    dateOption,
    timeZone,
    disableFutureDates = true,
  } = props;

  moment.tz.setDefault(timeZone);

  const [startDate] = useSelector(getSyncDates);

  const yearOptions: { label: string; value: string }[] = [];
  const startYear = moment(startDate).year() || 2020;
  const currentYear = moment().year();
  const currentMonth = moment().month();

  for (let i = currentYear; i >= startYear; i -= 1) {
    yearOptions.push({ label: i.toString(), value: i.toString() });
  }

  const [range, setRange] = useState<DateRange | undefined>(defaultRange);
  const [isMonthSelection, setIsMonthSelection] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());

  const [popperRef, setPopperRef] = useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(containerRef.current, popperRef, {
    placement: 'bottom-start',
  });

  useClickOutside({ current: popperRef }, () => setShowCalendar(false));

  useEffect(() => {
    setRange(defaultRange);
  }, [defaultRange]);

  useEffect(() => {
    if (range?.from?.getMonth()) {
      setSelectedMonth(range.from.getMonth());
    }
    if (range?.from?.getFullYear().toString()) {
      setSelectedYear(range?.from?.getFullYear().toString());
    }
  }, [range]);

  if (!containerRef.current) return null;

  const handleApplyClick = (): void => {
    if (range) {
      setDateRange(range);
    }
    setShowCalendar(false);
  };

  const handleCancelClick = (): void => {
    setShowCalendar(false);
  };

  const handleSelectInSingleMonth = (day: Date): void => {
    if (dateOption === 'after') {
      setRange({ from: day });
      setDateRange({ from: day });
    } else {
      setRange({ from: moment(0).add(1, 'day').utc().toDate(), to: day });
      setDateRange({ from: moment(0).add(1, 'day').utc().toDate(), to: day });
    }
  };

  const handleNextClick = (): void => {
    const date = moment({
      month: selectedMonth,
      year: Number.parseInt(selectedYear, 10),
      day: 1,
    });

    setRange({ from: date.toDate() });
    setIsMonthSelection(false);
  };

  if (dateOption === 'date-range' && range && range.to && !range.from) {
    setRange((r) => ({ from: r?.to, to: undefined }));
  }

  const renderDatePicker = (): React.ReactPortal | null => {
    const defaultClassnames = getDefaultClassNames();
    const portal = document.getElementById('dropdown-portal');

    const dateRangePicker = (): JSX.Element => {
      const monthYearPicker = (
        <div className="w-104 pl-3 pr-6 pt-6 pb-2 flex flex-col gap-8">
          <div className="flex flex-row gap-15">
            <div className="w-1/2 flex flex-col items-center gap-4">
              <span className="text-heading-3">Month</span>
              <div className="grid grid-cols-3 gap-2">
                {months.map((m, idx) => (
                  <button
                    key={m}
                    type="button"
                    className={`w-12.5 h-7.5 p-1 flex items-center justify-center text-small border border-litlingo-gray-2 rounded hover:border-litlingo-gray-3 focus:outline-none ${
                      selectedMonth === idx ? 'bg-litlingo-primary' : 'bg-white'
                    }`}
                    onClick={(): void => {
                      setSelectedMonth(idx);
                    }}
                  >
                    <span
                      className={`font-bold ${
                        selectedMonth === idx ? 'text-white' : 'text-litlingo-gray-5'
                      }`}
                    >
                      {m}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="w-1/2 flex flex-col items-center gap-4">
              <span className="text-heading-3">Year</span>
              <div className="w-full">
                <SelectRedesign
                  onChange={(value: string): void => {
                    setSelectedYear(value);
                  }}
                  options={yearOptions}
                  value={selectedYear}
                />
              </div>
            </div>
          </div>

          <div className="self-end flex flex-row gap-1">
            <button
              type="button"
              className="button button--secondary flex justify-center w-20 h-7"
              onClick={handleCancelClick}
            >
              <span className="font-bold">Cancel</span>
            </button>
            <button
              type="button"
              className="button button--primary flex justify-center w-20 h-7"
              onClick={handleNextClick}
            >
              Next
            </button>
          </div>
        </div>
      );

      const datePicker = (
        <>
          <button
            type="button"
            className="ml-2 mb-2 self-start text-small focus:outline-none"
            onClick={(): void => {
              setIsMonthSelection(true);
            }}
          >
            <span className="text-litlingo-primary underline">{`<`} Select month and year</span>
          </button>
          <DayPicker
            mode="range"
            selected={range}
            onSelect={(dateRange: DateRange | undefined): void => {
              // the idea is to change the closest to the changed range
              // if there is no from or to we just set
              if (!dateRange?.from || !dateRange.to) {
                setRange(dateRange);
                return;
              }

              if (!range?.from && !range?.to) {
                setRange({ from: dateRange.from, to: undefined });
                return;
              }

              // if there is no from or to we just set
              if (!range?.from || !range.to) {
                setRange(dateRange);
                return;
              }

              // if we have both we set only the changed one to reset
              if (range?.from !== dateRange.from) {
                setRange({ from: dateRange.from, to: undefined });
              } else {
                setRange({ from: undefined, to: dateRange.to });
              }
            }}
            numberOfMonths={2}
            defaultMonth={
              new TZDate(
                moment(range?.from).subtract(1, 'month').startOf('month').toDate(),
                timeZone
              )
            }
            disabled={{ after: moment().toDate() }}
            components={{
              NextMonthButton: CustomNextMonthButton,
            }}
            classNames={{
              day: `${defaultClassnames.day} text-small`,
            }}
            modifiersClassNames={{
              disabled: 'disabled-date',
            }}
            timeZone={timeZone}
          />
          <div className="self-end flex flex-row gap-1">
            <button
              type="button"
              className="button button--secondary flex justify-center w-20 h-7"
              onClick={handleCancelClick}
            >
              <span className="font-bold">Cancel</span>
            </button>
            <button
              type="button"
              className="button button--primary flex justify-center w-20 h-7"
              onClick={handleApplyClick}
              disabled={!range?.from && !range?.to}
            >
              Apply
            </button>
          </div>
        </>
      );

      return isMonthSelection ? monthYearPicker : datePicker;
    };

    const datePickerElement = (
      <div
        ref={(node): void => {
          setPopperRef(node);
        }}
        className="fixed flex flex-col gap-2 items-center min-w-54 px-3 pt-3 pb-2 mt-1 bg-white border border-litlingo-gray-2 rounded z-alert"
        style={{ ...styles.popper, filter: 'drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))' }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...attributes.popper}
        aria-hidden
        onClick={(e): void => e.stopPropagation()}
      >
        {dateOption === 'date-range' ? (
          dateRangePicker()
        ) : (
          <DayPicker
            mode="single"
            selected={dateOption === 'after' ? range?.from : range?.to}
            onSelect={(day: Date | undefined): void => {
              if (day) {
                handleSelectInSingleMonth(day);
              }
            }}
            numberOfMonths={1}
            defaultMonth={new TZDate(moment(range?.from).toDate(), timeZone)}
            disabled={disableFutureDates ? { after: moment().toDate() } : undefined}
            components={{
              NextMonthButton: (nextMonthProps) => (
                <CustomNextMonthButton
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...nextMonthProps}
                  single
                  disableNextMonth={disableFutureDates}
                />
              ),
            }}
            classNames={{
              day: `${defaultClassnames.day} text-small`,
            }}
            modifiersClassNames={{
              disabled: 'disabled-date',
            }}
            timeZone={timeZone}
          />
        )}
      </div>
    );

    if (portal) {
      return ReactDOM.createPortal(datePickerElement, portal);
    }
    return null;
  };

  return renderDatePicker();
};

export default DateRangePicker;
